/* Staff inventory page (sections/product-inventory.liquid).

   Loads the whole catalogue once from assets/inventory-stock.json - a snapshot built by
   inventory-page/build-inventory-snapshot.js, because Liquid cannot read per-location
   inventory quantities - then does all search / filtering / sorting client-side. 5,600
   products is small enough to keep in memory, which is why there is no server round-trip
   per keystroke.

   ONE SOURCE OF TRUTH. The snapshot is the only source; there is deliberately no second,
   live per-row lookup. The earlier version overlaid Liquid's store_availabilities on top and
   flagged disagreements as "drift", which meant two sources that could contradict each other
   and a whole class of false alarms when the overlay misfired. Freshness is now a scheduling
   problem, not a reconciliation problem: the cron rebuilds this file often, and the header
   states exactly how old it is. If the number in a chip is wrong, the fix is to run the
   snapshot job - never to second-guess it in the browser. */
(function () {
  var root = document.querySelector('[data-product-inventory]');
  if (!root) return;

  var PER_PAGE = parseInt(root.dataset.perPage, 10) || 60;
  var LOW = parseInt(root.dataset.lowStock, 10) || 2;
  var ADMIN = root.dataset.adminStore || '';
  var CARD_OPENS = root.dataset.cardOpens || 'admin';
  var SYM = root.dataset.currencySymbol || '';
  /* How old the snapshot may get before the header says so. With the snapshot as the only
     source this stamp is the sole trust signal on the page, so it should sit a little above
     the cron interval - long enough not to cry wolf, short enough to catch a dead job. */
  var STALE_MIN = parseInt(root.dataset.staleAfter, 10) || 90;

  var els = {
    sub: root.querySelector('[data-pinv-sub]'),
    q: root.querySelector('[data-pinv-q]'),
    clear: root.querySelector('[data-pinv-clear]'),
    list: root.querySelector('[data-pinv-list]'),
    state: root.querySelector('[data-pinv-state]'),
    more: root.querySelector('[data-pinv-more]'),
    summary: root.querySelector('[data-pinv-summary]'),
    sort: root.querySelector('[data-pinv-sort]'),
    reset: root.querySelector('[data-pinv-reset]'),
    exp: root.querySelector('[data-pinv-export]'),
    viewBtns: root.querySelectorAll('[data-pinv-view]')
  };
  var filterEls = {};
  Array.prototype.forEach.call(root.querySelectorAll('[data-pinv-filter]'), function (s) {
    filterEls[s.dataset.pinvFilter] = s;
  });

  var DATA = null;       // parsed snapshot
  var ALL = [];          // normalised product objects
  var view = [];         // current filtered + sorted set
  var shown = 0;

  /* ------------------------------------------------------------------ helpers */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(n) {
    var v = Number(n) || 0;
    try { return SYM + v.toLocaleString('en-IN', { maximumFractionDigits: 0 }); }
    catch (e) { return SYM + v; }
  }
  function ago(iso) {
    var then = new Date(iso).getTime();
    if (!then) return '';
    var mins = Math.round((Date.now() - then) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return mins + ' min ago';
    var hrs = Math.round(mins / 60);
    if (hrs < 24) return hrs + (hrs === 1 ? ' hour ago' : ' hours ago');
    var days = Math.round(hrs / 24);
    return days + (days === 1 ? ' day ago' : ' days ago');
  }
  /* The four location names share a long prefix ("Turakhia Opticians …", "Turakhia Vision
     Care …"); chips only have room for the distinguishing part. */
  function shortLoc(name) {
    return name.replace(/^Turakhia\s+(Opticians|Vision Care)\s*/i, '') ||
      name.replace(/^Turakhia\s+/i, '') || name;
  }
  function debounce(fn, ms) {
    var t; return function () {
      var a = arguments, c = this;
      clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms);
    };
  }

  /* ------------------------------------------------------------------ view
     List vs grid is pure CSS on the same markup, so switching costs no re-render.
     Remembered per device - it is a personal preference, not part of a shared filter URL. */
  var VIEW_KEY = 'pinv:view';
  function setView(v, persist) {
    v = v === 'grid' ? 'grid' : 'list';
    els.list.dataset.view = v;
    Array.prototype.forEach.call(els.viewBtns, function (b) {
      b.setAttribute('aria-pressed', b.dataset.pinvView === v ? 'true' : 'false');
    });
    if (persist) { try { localStorage.setItem(VIEW_KEY, v); } catch (e) { /* private mode */ } }
  }
  var stored = null;
  try { stored = localStorage.getItem(VIEW_KEY); } catch (e) { /* private mode */ }
  setView(stored || root.dataset.defaultView || 'grid', false);
  Array.prototype.forEach.call(els.viewBtns, function (b) {
    b.addEventListener('click', function () { setView(b.dataset.pinvView, true); });
  });

  /* ------------------------------------------------------------------ load */
  fetch(root.dataset.src, { cache: 'no-cache' })
    .then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    })
    .then(init)
    .catch(function (err) {
      els.sub.textContent = 'Could not load the inventory snapshot (' + err.message + ').';
      els.state.textContent =
        'assets/inventory-stock.json is missing or unreadable. Run: node inventory-page/build-inventory-snapshot.js --commit';
    });

  /* Vendor names in Shopify are free text, so the same brand arrives as "Ray Ban" and
     "Ray Ban " - trim them, and fold the Meta spellings into one brand. Display only:
     the store's vendor values are untouched. */
  var BRAND_ALIASES = {
    'Ray Ban Meta': 'Ray Ban X Meta',
    'Ray Ban Meta Eyewear': 'Ray Ban X Meta',
    'Oliver People': 'Oliver Peoples'
  };
  function brandName(v) {
    var b = String(v || '').trim().replace(/\s+/g, ' ');
    return BRAND_ALIASES[b] || b;
  }

  function init(data) {
    DATA = data;
    var base = data.imgbase || '';

    ALL = data.p.map(function (r) {
      var stock = r[9] || [];
      var total = stock.reduce(function (a, b) { return a + b; }, 0);
      var img = r[8] || '';
      return {
        id: r[0], handle: r[1], title: r[2], brand: brandName(r[3]), type: r[4], sku: r[5],
        price: r[6], status: r[7],
        img: img ? (/^https?:|^\/\//.test(img) ? img : base + img) : '',
        stock: stock, total: total, tags: r[10] || '', variants: r[11] || 1, tracked: r[12],
        // one lowercase haystack per product so a keystroke is a substring scan, not a rebuild
        hay: (r[2] + ' ' + r[3] + ' ' + r[4] + ' ' + r[5] + ' ' + r[1] + ' ' + (r[10] || ''))
          .toLowerCase().replace(/\|/g, ' ')
      };
    });

    fillOptions();
    readHash();
    apply();

    /* An unparseable or missing `synced` must read as stale, not as fresh - failing open here
       would present unknown-age numbers as current, which is the one thing this stamp exists
       to prevent. */
    var syncedAt = new Date(data.synced).getTime();
    var ageMin = syncedAt ? (Date.now() - syncedAt) / 60000 : Infinity;
    var stale = !(ageMin < STALE_MIN);
    els.sub.innerHTML =
      '<b>' + ALL.length.toLocaleString('en-IN') + '</b> products · stock synced ' +
      esc(syncedAt ? ago(data.synced) : 'at an unknown time') +
      (stale ? ' - the sync job may have stopped, numbers below may be out of date' : '');
    els.sub.dataset.stale = stale ? '1' : '0';
  }

  function fillOptions() {
    var brands = {}, types = {};
    ALL.forEach(function (p) {
      if (p.brand) brands[p.brand] = (brands[p.brand] || 0) + 1;
      if (p.type) types[p.type] = (types[p.type] || 0) + 1;
    });
    function fill(sel, counts) {
      if (!sel) return;
      Object.keys(counts).sort().forEach(function (k) {
        var o = document.createElement('option');
        o.value = k; o.textContent = k + ' (' + counts[k] + ')';
        sel.appendChild(o);
      });
    }
    fill(filterEls.brand, brands);
    fill(filterEls.type, types);
    if (filterEls.location) {
      (DATA.locations || []).forEach(function (name, i) {
        var o = document.createElement('option');
        o.value = String(i); o.textContent = name;
        filterEls.location.appendChild(o);
      });
    }
  }

  /* ------------------------------------------------------------------ filter + sort */
  function apply() {
    var terms = (els.q.value || '').toLowerCase().trim().split(/\s+/).filter(Boolean);
    var brand = filterEls.brand ? filterEls.brand.value : '';
    var type = filterEls.type ? filterEls.type.value : '';
    var locIdx = filterEls.location && filterEls.location.value !== '' ? parseInt(filterEls.location.value, 10) : -1;
    var stock = filterEls.stock ? filterEls.stock.value : '';
    var status = filterEls.status ? filterEls.status.value : '';

    view = ALL.filter(function (p) {
      if (brand && p.brand !== brand) return false;
      if (type && p.type !== type) return false;
      if (status && p.status !== status) return false;

      // a location filter scopes the stock test to that location
      var qty = locIdx >= 0 ? (p.stock[locIdx] || 0) : p.total;
      if (locIdx >= 0 && stock === '' && qty <= 0) return false;
      if (stock === 'in' && qty <= 0) return false;
      if (stock === 'out' && qty > 0) return false;
      if (stock === 'low' && (qty <= 0 || qty > LOW)) return false;

      for (var i = 0; i < terms.length; i++) {
        if (p.hay.indexOf(terms[i]) === -1) return false;
      }
      return true;
    });

    var s = els.sort ? els.sort.value : 'title';
    var dir = s.charAt(0) === '-' ? -1 : 1;
    var key = s.replace(/^-/, '');
    var locIdxSort = locIdx;
    view.sort(function (a, b) {
      var av, bv;
      if (key === 'stock') {
        av = locIdxSort >= 0 ? (a.stock[locIdxSort] || 0) : a.total;
        bv = locIdxSort >= 0 ? (b.stock[locIdxSort] || 0) : b.total;
      } else if (key === 'price') { av = a.price; bv = b.price; }
      else { return dir * a.title.localeCompare(b.title); }
      return dir * (av - bv);
    });

    els.clear.hidden = !els.q.value;
    shown = 0;
    els.list.innerHTML = '';
    renderMore();
    renderSummary(locIdx);
    writeHash();
  }

  function renderSummary(locIdx) {
    var units = 0, withStock = 0;
    view.forEach(function (p) {
      var q = locIdx >= 0 ? (p.stock[locIdx] || 0) : p.total;
      units += q;
      if (q > 0) withStock++;
    });
    var scope = locIdx >= 0 ? ' at ' + esc(DATA.locations[locIdx]) : '';
    els.summary.hidden = false;
    els.summary.innerHTML =
      '<span><b>' + view.length.toLocaleString('en-IN') + '</b> products</span>' +
      '<span><b>' + units.toLocaleString('en-IN') + '</b> units' + scope + '</span>' +
      '<span><b>' + withStock.toLocaleString('en-IN') + '</b> in stock' + scope + '</span>' +
      '<span><b>' + (view.length - withStock).toLocaleString('en-IN') + '</b> at zero</span>';
  }

  /* ------------------------------------------------------------------ render */
  function adminUrl(p) { return 'https://admin.shopify.com/store/' + ADMIN + '/products/' + p.id; }

  function rowHtml(p) {
    var locs = (DATA.locations || []).map(function (name, i) {
      var q = p.stock[i] || 0;
      return '<span class="pinv-loc" data-in="' + (q > 0 ? 1 : 0) + '" data-loc="' + i + '" title="' + esc(name) + '">' +
        '<span class="pinv-loc__dot"></span>' +
        '<span class="pinv-loc__name">' + esc(shortLoc(name)) + '</span>' +
        '<span class="pinv-loc__qty">' + q + '</span></span>';
    }).join('');

    var meta = [];
    if (p.brand) meta.push('<span>' + esc(p.brand) + '</span>');
    if (p.type) meta.push('<span>' + esc(p.type) + '</span>');
    meta.push('<span class="pinv-row__sku">' + (p.sku ? esc(p.sku) : 'no SKU') + '</span>');
    if (p.variants > 1) meta.push('<span>' + p.variants + ' variants</span>');
    if (p.status === 'D') meta.push('<span><span class="pinv-tag pinv-tag--draft">Draft</span></span>');
    if (p.status === 'R') meta.push('<span><span class="pinv-tag pinv-tag--archived">Archived</span></span>');
    if (!p.tracked) meta.push('<span><span class="pinv-tag pinv-tag--untracked">Untracked</span></span>');

    var thumb = p.img
      ? '<img class="pinv-row__thumb" src="' + esc(p.img) + '" alt="" loading="lazy" width="56" height="56">'
      : '<div class="pinv-row__thumb pinv-row__thumb--empty">no<br>image</div>';

    return '<div class="pinv-row" role="listitem" data-handle="' + esc(p.handle) + '"' +
      (CARD_OPENS === 'none' ? '' : ' data-clickable="1"') + '>' +
      thumb +
      '<div class="pinv-row__main">' +
        '<a class="pinv-row__title" href="' + esc(adminUrl(p)) + '" target="_blank" rel="noopener">' + esc(p.title) + '</a>' +
        '<div class="pinv-row__meta">' + meta.join('') + '</div>' +
        '<div class="pinv-locs">' + locs + '</div>' +
      '</div>' +
      '<div class="pinv-row__side">' +
        '<div class="pinv-row__total" data-zero="' + (p.total > 0 ? 0 : 1) + '">' + p.total + ' in stock' +
          '<small>' + money(p.price) + '</small></div>' +
        '<div class="pinv-row__actions">' +
          '<a class="pinv-row__edit" href="' + esc(adminUrl(p)) + '" target="_blank" rel="noopener">Edit</a>' +
          '<a class="pinv-row__view" href="/products/' + esc(p.handle) + '" target="_blank" rel="noopener" title="View on the store">View</a>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  function renderMore() {
    var slice = view.slice(shown, shown + PER_PAGE);
    if (slice.length) {
      var frag = document.createElement('div');
      frag.innerHTML = slice.map(rowHtml).join('');
      while (frag.firstChild) els.list.appendChild(frag.firstChild);
    }
    shown += slice.length;

    els.more.hidden = shown >= view.length;
    els.more.textContent = 'Load more (' + Math.max(0, view.length - shown).toLocaleString('en-IN') + ' left)';
    els.state.textContent = view.length ? '' : 'No products match those filters.';
  }

  /* ------------------------------------------------------------------ url state */
  function writeHash() {
    var parts = [];
    if (els.q.value) parts.push('q=' + encodeURIComponent(els.q.value));
    Object.keys(filterEls).forEach(function (k) {
      if (filterEls[k].value) parts.push(k + '=' + encodeURIComponent(filterEls[k].value));
    });
    if (els.sort && els.sort.value !== 'title') parts.push('sort=' + encodeURIComponent(els.sort.value));
    var hash = parts.join('&');
    history.replaceState(null, '', hash ? '#' + hash : window.location.pathname);
  }
  function readHash() {
    var h = window.location.hash.replace(/^#/, '');
    if (!h) return;
    h.split('&').forEach(function (pair) {
      var kv = pair.split('=');
      var k = kv[0], v = decodeURIComponent(kv[1] || '');
      if (k === 'q') els.q.value = v;
      else if (k === 'sort' && els.sort) els.sort.value = v;
      else if (filterEls[k]) filterEls[k].value = v;
    });
  }

  /* ------------------------------------------------------------------ export */
  function exportCsv() {
    var head = ['handle', 'title', 'brand', 'type', 'sku', 'price', 'status']
      .concat(DATA.locations).concat(['total', 'admin_url']);
    var cell = function (v) {
      var s = String(v == null ? '' : v);
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    var lines = [head.map(cell).join(',')];
    view.forEach(function (p) {
      lines.push([p.handle, p.title, p.brand, p.type, p.sku, p.price, p.status]
        .concat(p.stock).concat([p.total, adminUrl(p)]).map(cell).join(','));
    });
    var blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    var a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'inventory-' + new Date().toISOString().slice(0, 10) + '.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(function () { URL.revokeObjectURL(a.href); }, 1000);
  }

  /* ------------------------------------------------------------------ events */
  els.q.addEventListener('input', debounce(apply, 120));
  els.clear.addEventListener('click', function () { els.q.value = ''; els.q.focus(); apply(); });
  Object.keys(filterEls).forEach(function (k) { filterEls[k].addEventListener('change', apply); });
  if (els.sort) els.sort.addEventListener('change', apply);
  els.more.addEventListener('click', renderMore);
  if (els.exp) els.exp.addEventListener('click', exportCsv);
  if (els.reset) els.reset.addEventListener('click', function () {
    els.q.value = '';
    Object.keys(filterEls).forEach(function (k) { filterEls[k].value = ''; });
    if (els.sort) els.sort.value = 'title';
    apply();
  });
  // clicking anywhere on the row opens the editor; inner links keep their own behaviour
  els.list.addEventListener('click', function (e) {
    if (CARD_OPENS === 'none') return;
    if (e.target.closest('a')) return;
    var row = e.target.closest('.pinv-row');
    if (!row) return;
    var handle = row.dataset.handle;
    var link = row.querySelector(CARD_OPENS === 'admin' ? '.pinv-row__edit' : '.pinv-row__view');
    if (link) window.open(link.href, '_blank', 'noopener');
    else if (handle) window.open('/products/' + handle, '_blank', 'noopener');
  });
})();
